import { ButtonGroup } from '@protonradio/proton-ui';
import { isToday } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import ActionBar from 'components/ActionBar';
import Button from 'components/Button';
import EntityGrid from 'components/EntityElement/EntityGrid';
import NoDataDisplay from 'components/NoDataDisplay';
import { Artist } from 'graphql/types';
import { isMobile } from 'helpers';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  getLabelArtists as getLabelArtistsAction,
  getLabelTopArtists as getLabelTopArtistsAction
} from 'redux/actions/labels';
import {
  selectLabelArtistsByLabelIdRoot,
  selectLabelTopArtistsByLabelIdRoot
} from 'redux/selectors';

import { getFilteredArtists } from './helpers';

type LabelArtistsProps = {
  match: {
    params: {
      id: string;
      name: string;
      tab: string;
    };
  };
};

type LabelArtistsState = {
  __lastFetched: string;
  __isFetching: boolean;
  data: [Artist];
};

const RELEASE_GRID_PROPS = {
  type: EntityGrid.TYPES.ARTIST,
  size: EntityGrid.SIZES.LARGE
};

const LabelArtists = (props: LabelArtistsProps) => {
  const { match } = props;
  const { id: labelId, name, tab } = match.params;

  const dispatch = useAppDispatch();
  const artists = useAppSelector<LabelArtistsState>(state =>
    selectLabelArtistsByLabelIdRoot(state, { labelId })
  );
  const topArtists = useAppSelector<LabelArtistsState>(state =>
    selectLabelTopArtistsByLabelIdRoot(state, { labelId })
  );

  const [searchText, setSearchText] = useState<string>('');

  const history = useHistory();

  useEffect(() => {
    if (!isToday(new Date(artists.__lastFetched))) {
      void dispatch(getLabelArtistsAction(labelId));
    }

    if (!isToday(new Date(topArtists.__lastFetched))) {
      void dispatch(getLabelTopArtistsAction(labelId, { limit: 20 }));
    }
  }, [labelId, artists.__lastFetched, topArtists.__lastFetched, dispatch]);

  const currentData = tab === 'top' ? topArtists.data : artists.data;
  const filteredArtists = useMemo(
    () => getFilteredArtists(currentData, searchText),
    [currentData, searchText]
  );

  const isLoading =
    (tab === 'all' && artists.__isFetching) || (tab === 'top' && topArtists.__isFetching);

  return (
    <>
      <ActionBar
        searchInput={{
          onChange: e => setSearchText(e.target.value),
          placeholder: 'Search Artists',
          value: searchText
        }}
        renderActions={() => (
          <div className={isMobile() ? 'mb-2' : undefined} style={{ width: 200 }}>
            <ButtonGroup
              value={match.params.tab}
              onChange={value => {
                history.push(`/labels/${labelId}/${name}/artists/${value}`);
              }}
            >
              <ButtonGroup.Option value="top">Top</ButtonGroup.Option>
              <ButtonGroup.Option value="all">All</ButtonGroup.Option>
            </ButtonGroup>
          </div>
        )}
        loading={isLoading}
      />

      {!isLoading && filteredArtists.length === 0 ? (
        <NoDataDisplay
          text={
            !artists.data.length
              ? 'This label has no current artists.'
              : 'This label has no artists matching your search.'
          }
        >
          {artists && searchText.length > 0 && (
            <Button rounded onClick={() => setSearchText('')}>
              Clear Search
            </Button>
          )}
        </NoDataDisplay>
      ) : (
        <EntityGrid data={filteredArtists} loading={isLoading} {...RELEASE_GRID_PROPS} />
      )}
    </>
  );
};

export default LabelArtists;
